import React, { useEffect, useState } from 'react';
import { ChatStamp } from '../chat-stamp/ChatStamp';
import { Block } from 'baseui/block';
import { useStyletron } from 'baseui';
import { CustomTheme } from '../baseweb-custom-theme/customTheme';
import { InlineDialog } from '@relaynetwork/design-system';
import { ChatBubbleGroup } from '../chat-bubble-group/ChatBubbleGroup';
import { ChatTranslationStamp } from '../chat-translation-stamp/ChatTranslationStamp';
import { ChatUserStamp } from '../chat-user-stamp/ChatUserStamp';

const __variant = ['user', 'time', 'user-time'] as const;
export type Variant = typeof __variant[number];

export enum Role {
  Agent = 'Agent',
  Customer = 'Customer',
}

export enum TranslationMap {
  'es' = 'Spanish',
  'en' =  'English',
  'ru' = 'Russian',
  'ar' = 'Arabic',
  'zh' = 'Chinese',
  'vi' = 'Vietnamese',
  'ne' = 'Nepali',
}

export interface ChatItemProps {
  stampVariant: Variant;
  user: string;
  timestamp: string;
  role: Role;
  text: string;
  translatedText?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  error?: string;
  isTranslationEnabled?: boolean;
  showEnableTranslationOption?: boolean;
  isRMTranslationsEnabled?: boolean;
  onEnableTranslationClick: () => void;
  translatedLanguageCode?: string;
  hasMessageTranslationError:boolean;
  enableTranslationError?: { error: boolean; message: string};
  dataTestId: string;
}

export const ChatItem = ({
  stampVariant,
  user,
  timestamp,
  role,
  text,
  translatedText,
  style,
  error,
  isTranslationEnabled = false,
  showEnableTranslationOption = false,
  isRMTranslationsEnabled = false,
  onEnableTranslationClick,
  translatedLanguageCode,
  hasMessageTranslationError,
  enableTranslationError,
  dataTestId,
}: ChatItemProps) => {
  const [, theme] = useStyletron();
  const customTheme = theme as CustomTheme;
  const [showOriginal, setShowOriginal] = useState(translatedText ? false : true);
  // if message has translations, default to showing translated text in the original chat bubble
  // if message does not have translations, show original text without "original message" toggle option
  useEffect(() => {
    setShowOriginal(translatedText ? false : true);
  }, [translatedText]);
  // const textOg = role === Role.Agent ? text : 'Hola, ¿cómo puedo ayudar?';
  // const translatedText =
  //   role === Role.Agent ? 'Hola, ¿cómo puedo ayudar?' : text;
  const formattedDate = new Date(timestamp).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  return (
    <div>
      <ChatBubbleGroup
        role={role}
        text={text} // uncomment textOg above and replace it here to test dummy mock data for chatbubble variant
        translatedText={translatedText}
        isHideOriginal={!showOriginal}
        isRmTranslationsEnabled={isRMTranslationsEnabled}
      />
      <Block
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        overrides={{
          Block: {
            style: () => ({
              paddingLeft: customTheme.sizing.notificationChatItemPadding,
              marginTop: customTheme.sizing.notificationChatItemMargin,
            }),
            props: {
              dir: 'auto',
            },
          },
        }}
      >
        {error && error.length > 0 ? (
          <InlineDialog
            variant="alert"
            text={error}
            status="error"
            textSize="small"
          ></InlineDialog>
        ) : (
          <div data-testid="chat-user">
            <ChatUserStamp
              primaryText={user}
              secondaryText={formattedDate}
            ></ChatUserStamp>
          </div>
        )}
        {isRMTranslationsEnabled && isTranslationEnabled && translatedText && (
          <ChatTranslationStamp
            infoText={
              hasMessageTranslationError
                ? ''
                : TranslationMap[translatedLanguageCode as keyof typeof TranslationMap]
                ? `This message was sent in ${TranslationMap[translatedLanguageCode as keyof typeof TranslationMap]}.`
                : 'This message has been translated.'
            }
            errorText={
              hasMessageTranslationError
                ? 'Failed to provide translation for this language'
                : ''
            }
            onClick={() => setShowOriginal(!showOriginal)}
            dataTestId={`${dataTestId}-toggle-translation-view`}
            actionText={
              role === Role.Agent
                ? showOriginal
                  ? 'Hide Sent Message'
                  : 'View Sent Message'
                : showOriginal
                ? 'Hide Original'
                : 'View Original'
            }
          />
        )}
        {isRMTranslationsEnabled &&
          showEnableTranslationOption &&
          !isTranslationEnabled && (
            <ChatTranslationStamp
              onClick={() => onEnableTranslationClick()}
              errorText={
                enableTranslationError?.error &&
                enableTranslationError?.message === 'Locale not supported'
                  ? 'Failed to provide translation for this language'
                  : enableTranslationError?.error &&
                    enableTranslationError?.message !== 'Locale not supported'
                  ? 'Failed to provide translation for this language'
                  : ''
              }
              dataTestId={`enable-translation`}
              actionText={
                enableTranslationError?.error &&
                enableTranslationError?.message === 'Locale not supported'
                  ? ''
                  : enableTranslationError?.error &&
                    enableTranslationError?.message !== 'Locale not supported'
                  ? 'Try again'
                  : 'Enable Translation'
              }
            />
          )}
      </Block>
    </div>
  );
};
